import type { NavItems } from '@ksyos/design-system';
import { SideNav } from '@ksyos/design-system';
import { differenceBy, sortBy, uniqBy } from 'lodash';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { IEpisodeCategory, IServicesByServiceGroupForEpisodeCategory } from '@common/interfaces/';
import type { IProvidedServicesAndCompensationPayouts, IServiceConsumer } from '@frontend/components/interfaces';
import type { IReduxState } from '@frontend/configureStore';
import { EpisodeCategory, ManagerAuthorization, ServiceCode } from '@common/enums/';
import { UrlBuilder } from '@common/utils/urlBuilder';
import { SideNavItem } from '@frontend/components/elements/navigation/components/SideNavItem';
import { fetchJson } from '@frontend/store/fetch';
import { getSelectedLanguage } from '@frontend/translate';
import { getCarePathMenuItemsWithoutFetch, hasOneAuthorization, useUiStateContext } from '@frontend/utils';
import { hasEmploymentEpisodeCategories } from '../../elements/EpisodeCategoryButtonRow';

export interface IPersonalSettingsNavigationProps {
    employmentId: string;
    hasInactiveEpisodeCategories: boolean;
    inactiveEpisodeCategories: IEpisodeCategory[];
    isActiveEmployment?: boolean;
    isCurrentUser: boolean;
    isReadOnly: boolean;
    organizationId: string;
    preferredServiceConsumerEpisodeCategories: IEpisodeCategory[];
    preferredServiceProviderEpisodeCategories: IEpisodeCategory[];
    serviceConsumerEpisodeCategories: IEpisodeCategory[];
    serviceConsumers: IServiceConsumer[];
    serviceEmploymentEpisodeCategories: IEpisodeCategory[];
    serviceProviderEpisodeCategories: IEpisodeCategory[];
    serviceReaderEpisodeCategories: IEpisodeCategory[];
    serviceSupervisorEpisodeCategories: IEpisodeCategory[];
}

export function PersonalSettingsNavigation(props: IPersonalSettingsNavigationProps) {
    const translate = getSelectedLanguage();
    const isManager = useSelector((state: IReduxState) => state.authentication.isManager);
    const [navigationItems, setNavigationItems] = useState<NavItems[]>([]);
    const [serviceGroupsByEpisodeCategory, setServiceGroupsByEpisodeCategory] = useState<
        IServicesByServiceGroupForEpisodeCategory[] | undefined
    >();
    const { uiState, dispatchUiStateAction } = useUiStateContext();

    useEffect(() => {
        getServiceGroupsByEpisodeCategory();
    }, []);

    useEffect(() => {
        if (serviceGroupsByEpisodeCategory) {
            getNavigationItems();
        }
    }, [
        serviceGroupsByEpisodeCategory,
        props.serviceConsumerEpisodeCategories,
        props.serviceProviderEpisodeCategories,
        props.isReadOnly,
    ]);

    return (
        <SideNav>
            {navigationItems.map((item) => (
                <SideNavItem
                    defaultOpenKey="personalSettings"
                    item={item}
                    key={item.key}
                    onClose={handleClose}
                />
            ))}
        </SideNav>
    );

    async function getServiceGroupsByEpisodeCategory() {
        const result = await fetchJson(
            `/api/serviceGroupMemberships?organizationId=${props.organizationId}&groupByEpisodeCategory=true`,
            'GET'
        );
        setServiceGroupsByEpisodeCategory(result.json.items);
    }

    async function getNavigationItems() {
        let itemList = [...loadCarePathNavigationItems(), ...(await getCompensationPayoutsNavigationItems())];

        if (hasOneAuthorization(ManagerAuthorization.employmentSettingsRead) || props.isCurrentUser) {
            itemList = itemList.concat(await getEmailNotificationNavigationItems());
        }

        if (hasOneAuthorization(ManagerAuthorization.employmentSettingsRead)) {
            itemList = itemList.concat([
                ...getProviderPreferenceMenuItems(),
                ...getGgzEpisodeCategoryNavigationItem([ServiceCode.GGZCt]),
            ]);
        }

        if (props.isCurrentUser) {
            itemList = itemList.concat([
                ...getAllPreferenceNavigationItems(),
                ...getGgzEpisodeCategoryNavigationItem([ServiceCode.GGZCt, ServiceCode.GGZOembl, ServiceCode.GGZOint]),
            ]);
        }

        const items: NavItems[] = [
            {
                items: [...itemList],
                key: 'personalSettings',
                label: translate.common.personalSettings,
                to: `/employments/${props.employmentId}/personalSettings`,
                variant: 'link',
            },
        ];
        setNavigationItems(items);
    }

    function loadCarePathNavigationItems(): NavItems[] {
        if (!hasOneAuthorization(ManagerAuthorization.employmentSettingsRead)) {
            return [];
        }

        return getCarePathMenuItemsWithoutFetch(
            uniqueEpisodeCategories(),
            `/employments/${props.employmentId}/personalSettings`,
            props.hasInactiveEpisodeCategories
        );
    }

    async function getEmailNotificationNavigationItems(): Promise<NavItems[]> {
        if (await hasEmploymentEpisodeCategories(props.employmentId)) {
            return [
                {
                    key: 'serviceEmailNotifications',
                    label: translate.personalSettings.emailNotifications,
                    to: getNavigationSettingLink('serviceEmailNotifications'),
                    variant: 'link',
                },
            ];
        }

        return [];
    }

    function getAllPreferenceNavigationItems(): NavItems[] {
        const preferredServiceConsumerEpisodeCategories = props.isActiveEmployment
            ? props.preferredServiceConsumerEpisodeCategories.filter((episodeCategory) => episodeCategory.isActive)
            : props.preferredServiceConsumerEpisodeCategories;
        const preferredServiceProviderEpisodeCategories = props.isActiveEmployment
            ? props.preferredServiceProviderEpisodeCategories.filter((episodeCategory) => episodeCategory.isActive)
            : props.preferredServiceProviderEpisodeCategories;

        const episodeCategoriesServiceConsumers = createConsumerMenuItemsList(
            preferredServiceConsumerEpisodeCategories
        );
        const episodeCategoriesServiceProviders = createProviderMenuItemsList(
            preferredServiceProviderEpisodeCategories
        );
        let allItems = [...episodeCategoriesServiceConsumers, ...episodeCategoriesServiceProviders];

        if (!allItems.length) {
            return [];
        }

        allItems = sortBy(allItems, (item) => item.label);

        return [
            {
                items: allItems,
                key: 'preferenceList',
                label: translate.personalSettings.preferenceList.preferenceList,
                variant: 'button',
            },
        ];
    }

    function getProviderPreferenceMenuItems(): NavItems[] {
        let episodeCategoriesServiceProviders = createProviderMenuItemsList(
            props.preferredServiceProviderEpisodeCategories
        );

        if (!episodeCategoriesServiceProviders.length) {
            return [];
        }

        episodeCategoriesServiceProviders = sortBy(episodeCategoriesServiceProviders, (item) => item.label);

        return [
            {
                items: episodeCategoriesServiceProviders,
                key: 'preferenceList',
                label: translate.personalSettings.preferenceList.preferenceList,
                variant: 'button',
            },
        ];
    }

    function createConsumerMenuItemsList(episodeCategories: IEpisodeCategory[]): NavItems[] {
        return episodeCategories.map((category) => {
            let label = translate.episodeCategoryEnum[category.name];

            if (isManager) {
                const suffix = translate.episodeCategory.executors;
                label = `${label} ${suffix}`;
            }
            const key = `preferenceList-${category.name}-consumer`;
            const to = getNavigationPreferenceLink(category.name);

            return { key, label, to, variant: 'link' };
        });
    }

    function createProviderMenuItemsList(
        episodeCategories: (IEpisodeCategory & { serviceProviderId?: string })[]
    ): NavItems[] {
        return episodeCategories.map((category) => {
            let label = translate.episodeCategoryEnum[category.name];

            if (isManager) {
                const suffix = translate.episodeCategory.applicants;
                label = `${label} ${suffix}`;
            }
            const key = `preferenceList-${category.name}-provider`;

            const linkBase = getNavigationPreferenceLink(category.name);
            const linkAppendix = '/serviceProvider';
            const to = `${linkBase}${linkAppendix}`;

            return { key, label, to, variant: 'link' };
        });
    }

    function getGgzEpisodeCategoryNavigationItem(mentalHealthServiceCodes: ServiceCode[]): NavItems[] {
        const showGgzLink = props.serviceConsumers.some((consumer) =>
            mentalHealthServiceCodes.includes(consumer.service.code!)
        );

        if (showGgzLink) {
            return [
                {
                    key: 'mentalHealth',
                    label: translate.episodeCategoryEnum.mentalHealth,
                    to: getNavigationServiceConsumerLink(EpisodeCategory.mentalHealth),
                    variant: 'link',
                },
            ];
        }

        return [];
    }

    async function getCompensationPayoutsNavigationItems(): Promise<NavItems[]> {
        const providedEpisodeCategories = uniqueEpisodeCategoriesForCompensationPayoutList();
        if (
            providedEpisodeCategories.length === 0 ||
            !hasOneAuthorization(ManagerAuthorization.employmentSettingsRead)
        ) {
            return [];
        }
        const items: NavItems[] = [];
        for (const episodeCategory of providedEpisodeCategories) {
            const url = new UrlBuilder('/api/compensationPayouts/providedServicesAndCompensationPayouts');
            url.add('episodeCategory', episodeCategory.name);
            url.add('employmentId', props.employmentId);
            url.add('excludeServiceTypeOther', true);
            const result = await fetchJson(url.build(), 'GET');

            const providedServices: IProvidedServicesAndCompensationPayouts = result.json.item;
            if (providedServices.serviceGroupMemberships.length) {
                items.push({
                    key: episodeCategory.id,
                    label: translate.episodeCategoryEnum[episodeCategory.name],
                    to: `/employments/${props.employmentId}/personalSettings/compensationPayouts/${episodeCategory.name}`,
                    variant: 'link',
                });
            }
        }
        items.sort((a, b) => (a.label < b.label ? -1 : 1));
        if (items.length) {
            return [
                {
                    items,
                    key: 'compensationPayouts',
                    label: translate.compensationPayouts.fees,
                    variant: 'button',
                },
            ];
        }

        return [];
    }

    function getNavigationSettingLink(setting: 'serviceEmailNotifications' | 'compensationPayouts') {
        return `/employments/${props.employmentId}/personalSettings/${setting}`;
    }

    function getNavigationPreferenceLink(episodeCategory: EpisodeCategory) {
        return `/employments/${props.employmentId}/personalSettings/preferenceList/${episodeCategory}`;
    }

    function getNavigationServiceConsumerLink(episodeCategory: EpisodeCategory) {
        return `/employments/${props.employmentId}/personalSettings/episodeCategory/${episodeCategory}`;
    }

    function handleClose() {
        if (uiState.isTopBarOpen) {
            dispatchUiStateAction({ isTopBarOpen: false, type: 'setIsTopBarOpen' });
        }
    }

    function uniqueEpisodeCategoriesForCompensationPayoutList() {
        return uniqBy(
            [
                ...props.serviceEmploymentEpisodeCategories,
                ...props.serviceConsumerEpisodeCategories,
                ...props.serviceProviderEpisodeCategories,
                ...props.serviceSupervisorEpisodeCategories,
            ],
            'name'
        );
    }

    function uniqueEpisodeCategories() {
        const allEpisodeCategories = uniqBy(
            [
                ...props.serviceEmploymentEpisodeCategories,
                ...props.serviceConsumerEpisodeCategories,
                ...props.serviceProviderEpisodeCategories,
                ...props.serviceReaderEpisodeCategories,
                ...props.serviceSupervisorEpisodeCategories,
            ],
            'name'
        );

        return differenceBy(allEpisodeCategories, props.inactiveEpisodeCategories, 'name');
    }
}
